import React from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { LayoutDashboard, Sparkles, ArrowRight, LogOut, ShieldCheck } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_736b5617-324a-4027-a0b5-949393e5cf90/artifacts/te4le6nd_Untitled%20design.png";

/**
 * Post-login workspace chooser for admins / super-admins.
 * Lets an admin decide between the Admin Console and the same Member Portal
 * every member uses (with their unlimited admin access). Regular clients never
 * see this — they go straight to the portal.
 */
export default function ChooseWorkspace() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0F1C]">
        <div className="w-12 h-12 border-4 border-[#F97316] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  const isAdmin = user.role === 'admin' || user.role === 'super_admin';
  if (!isAdmin) return <Navigate to="/portal" replace />;

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const cards = [
    {
      to: '/admin',
      testid: 'choose-admin',
      Icon: LayoutDashboard,
      title: 'Admin Console',
      desc: 'Run the business: leads, bookings, clients, payments, Social Studio controls, automation and analytics.',
      accent: '#1D4ED8',
      cta: 'Enter Admin Console',
    },
    {
      to: '/portal',
      testid: 'choose-portal',
      Icon: Sparkles,
      title: 'Member Portal',
      desc: 'The exact member experience your users get: dashboard, sessions, programs and the Social Studio, with your unlimited admin access.',
      accent: '#F97316',
      cta: 'Enter Member Portal',
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white flex flex-col" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="choose-workspace">
      <header className="px-6 sm:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="DCA Enterprises" className="h-12 w-auto" />
        </div>
        <button onClick={handleLogout} data-testid="choose-logout"
          className="inline-flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white transition-colors">
          <LogOut size={16} /> Log out
        </button>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 pb-16">
        <div className="text-center mb-10 max-w-2xl">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-xs font-bold uppercase tracking-wider text-[#F97316] mb-4">
            <ShieldCheck size={14} /> {user.role === 'super_admin' ? 'Super Admin' : 'Admin'} access
          </span>
          <h1 className="text-3xl sm:text-4xl font-black mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome back, {user.name?.split(' ')[0] || 'there'}.
          </h1>
          <p className="text-base text-[#94A3B8]">Where would you like to go? You can switch between the two any time.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5 w-full max-w-3xl">
          {cards.map(({ to, testid, Icon, title, desc, accent, cta }) => (
            <Link key={to} to={to} data-testid={testid}
              className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-7 flex flex-col transition-all duration-300 hover:bg-white/[0.06] hover:-translate-y-1"
              style={{ boxShadow: '0 1px 0 rgba(255,255,255,0.04) inset' }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = accent; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}>
              <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: accent }}>
                <Icon size={22} className="text-white" />
              </span>
              <h2 className="text-xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
              <p className="text-sm text-[#94A3B8] leading-relaxed mb-6 flex-1">{desc}</p>
              <span className="inline-flex items-center gap-2 text-sm font-bold transition-transform group-hover:translate-x-1" style={{ color: accent }}>
                {cta} <ArrowRight size={16} />
              </span>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
